import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";

const PILLARS = [
  { n: 1, label: "Understanding Menopause", tint: "bg-rose-50" },
  { n: 2, label: "Symptoms", tint: "bg-amber-50" },
  { n: 3, label: "Treatments", tint: "bg-emerald-50" },
  { n: 4, label: "Healthy Ageing", tint: "bg-sky-50" },
  { n: 5, label: "Lifestyle", tint: "bg-violet-50" },
  { n: 6, label: "Mental Health", tint: "bg-pink-50" },
  { n: 7, label: "Relationships", tint: "bg-orange-50" },
  { n: 8, label: "Diversity", tint: "bg-teal-50" },
  { n: 9, label: "Medicines", tint: "bg-indigo-50" },
  { n: 10, label: "Practical Guides", tint: "bg-lime-50" },
];

type Topic = { id: string; slug: string; title: string; pillar: number; topic_type: string; summary_plain: string; evidence_level: string | null };

export default function KnowledgeHub() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("knowledge_topics").select("id, slug, title, pillar, topic_type, summary_plain, evidence_level")
      .eq("status", "approved").order("pillar").order("title")
      .then(({ data }) => { setTopics((data ?? []) as Topic[]); setLoading(false); });
  }, []);

  const filtered = q.trim()
    ? topics.filter(t => (t.title + " " + t.summary_plain).toLowerCase().includes(q.toLowerCase()))
    : topics;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto max-w-6xl px-4 py-10">
        <div className="text-center mb-8">
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Her Season Knowledge</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-3">Everything you might want to understand</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Evidence-based, plain-language guidance on menopause, symptoms, treatments and healthy ageing —
            written for you, reviewed against NICE, NAMS, AMS and Jean Hailes.
          </p>
        </div>

        <div className="relative max-w-xl mx-auto mb-10">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search topics — e.g. brain fog, HRT, sleep" value={q} onChange={e => setQ(e.target.value)} />
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
        ) : PILLARS.map(p => {
          const group = filtered.filter(t => t.pillar === p.n);
          if (group.length === 0) return null;
          return (
            <section key={p.n} className="mb-10">
              <h2 className="font-serif text-2xl mb-4">{p.n}. {p.label}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.map(t => (
                  <Link key={t.id} to={`/knowledge/${t.slug}`}>
                    <Card className={`h-full hover:shadow-md transition-shadow ${p.tint}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">{t.topic_type}</Badge>
                        </div>
                        <div className="font-serif text-lg leading-tight">{t.title}</div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{t.summary_plain}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {!loading && filtered.length === 0 && (
          <p className="text-center text-muted-foreground py-12">No topics match yet — we're actively building out this library.</p>
        )}
      </div>
    </div>
  );
}
